import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  createTheme,
  ThemeProvider,
  useTheme,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import AddColumnToTabRibbon from './AddColumnToTabRibbon';
import TabEditVehicleColumn from '../GroupDepartmentSettings/TabEditVehicleColumn';
import { updateTabProperty, updateTabPropertiesOrder, deleteTabProperty } from '../../../actions/apiDealerUpdates';
import { deleteVehicleColumn, updateVehicleColumn, addNewVehicleColumn } from '../../../actions/apiDepartmentUpdates';
import ConfirmationModal from '../../../components/ConfirmationDeleteModal';

const TabEditTabProperties = ({ tab }) => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [error, setError] = useState(null);
  const [localVehicleColumns, setLocalVehicleColumns] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [inputName, setInputName] = useState('');
  const [addColumnError, setAddColumnError] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const vehicle_columns = useSelector((state) => state.settings_departments.vehicle_columns);
  const related_installations = useSelector(
    (state) => state.settings_dealerships.related_installations,
  );
  const input_types = useSelector((state) => state.settings_departments.input_types);

  useEffect(() => {
    setLocalVehicleColumns(vehicle_columns || []);
  }, [vehicle_columns]);

  // Only increment tableKey for structural changes (add/delete columns)
  useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [vehicle_columns?.length]);

  useEffect(() => {
    if (!tab) {
      console.warn('TabEditTabProperties received undefined tab prop');
    }
  }, [tab]);

  const iconList = useMemo(
    () => [
      'far fa-paperclip',
      'far fa-clock',
      'far fa-alarm-clock',
      'far fa-cogs',
      'far fa-usd-square',
      'far fa-dollar-sign',
      'far fa-camera-retro',
      'far fa-camera-alt',
      'far fa-clipboard-list',
      'far fa-car',
      'far fa-clipboard-check',
      'far fa-tasks',
      'far fa-chart-bar',
      'far fa-ellipsis-h-alt',
      'far fa-receipt',
      'far fa-calendar-plus',
      'far fa-check-circle',
    ],
    [],
  );

  const inputTypesSorted = useMemo(() => {
    const newObj = Object.keys(input_types).map((key) => input_types[key]);
    return newObj.sort((a, b) => a.name.localeCompare(b.name));
  }, [input_types]);

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: '#007bff',
          },
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': {
                  color: '#007bff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 123, 255, 0.04)',
                },
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  const tabPropertiesColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Property Name',
        size: 150,
        enableEditing: false,
      },
      {
        accessorKey: 'dealership',
        header: 'Dealerships',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const dealershipString = row.original.dealership[0] || '';
          return dealershipString.split(',').includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {Array.isArray(related_installations) && related_installations.length > 0 ? (
                related_installations.map((option) => (
                  <MenuItem key={option.id} value={option.subdomain}>
                    {option.softname || option.subdomain}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No dealerships available</MenuItem>
              )}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row }) => {
          const dealershipArray =
            row.original.dealership[0] && typeof row.original.dealership[0] === 'string'
              ? row.original.dealership[0].split(',').filter(Boolean)
              : [];
          return {
            select: true,
            multiple: true,
            value: dealershipArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 300 },
                },
              },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: Array.isArray(related_installations) && related_installations.length > 0 ? (
              related_installations.map((option) => (
                <MenuItem key={option.id} value={option.subdomain}>
                  <Checkbox checked={dealershipArray.includes(option.subdomain)} />
                  {option.softname || option.subdomain}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No dealerships available</MenuItem>
            ),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value)
                ? event.target.value
                : typeof event.target.value === 'string' && event.target.value
                ? event.target.value.split(',')
                : [];
              const data = {
                id: row.original.tab_property_id[0],
                dealership: selectedValues.join(','),
              };
              dispatch(updateTabProperty(row.original.tab_property_id[0], data, 'editTabPropertyError'));
            },
            onClick: (event) => event.stopPropagation(),
          };
        },
        Cell: ({ row }) => {
          const dealerships =
            row.original.dealership[0] && typeof row.original.dealership[0] === 'string'
              ? row.original.dealership[0].split(',').filter(Boolean)
              : [];
          const displayNames = Array.isArray(related_installations)
            ? dealerships
                .map((subdomain) => {
                  const option = related_installations.find((opt) => opt.subdomain === subdomain);
                  return option ? option.softname || option.subdomain : subdomain;
                })
                .join(', ')
            : 'None';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {displayNames || 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'delete',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="error"
            size="small"
            className="btn btn-danger btn-sm"
            onClick={() => {
              setDeleteType('tab_property');
              setDeleteId(row.original.tab_property_id[0]);
              setDeleteName(row.original.name[0]);
              setModalOpen(true);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [related_installations, dispatch],
  );

  const vehicleColumns = useMemo(
    () => [
      {
        accessorKey: 'addto',
        header: 'Add +',
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {tab ? <AddColumnToTabRibbon vehicle_column={row} tab={tab} /> : null}
          </Box>
        ),
      },
      {
        accessorKey: 'icon',
        header: 'Icon',
        enableEditing: true,
        editVariant: 'select',
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.icon[0] || '',
          onChange: async (event) => {
            const iconValue = event.target.value;
            const id = row.original.vehicle_column_id[0];
            setLocalVehicleColumns((prev) =>
              prev.map((col) =>
                col.id === id ? { ...col, icon: iconValue } : col,
              ),
            );
            try {
              await dispatch(updateVehicleColumn(id, { icon: iconValue }, 'editVehicleColumnError'));
              setError(null);
              table.setEditingCell(null);
            } catch (ex) {
              setLocalVehicleColumns(vehicle_columns || []);
              setError('Failed to update icon. Please try again.');
              console.error('Icon update error:', ex);
            }
          },
          onClick: (event) => event.stopPropagation(),
          SelectProps: {
            renderValue: (selected) => (selected ? <i className={selected} /> : 'None'),
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 300 },
              },
            },
            sx: { zIndex: 1000 },
          },
          children: [
            <MenuItem key="none" value="">
              None
            </MenuItem>,
            ...iconList.map((icon) => (
              <MenuItem key={icon} value={icon}>
                <i className={icon} style={{ marginRight: '8px' }} />
                {icon}
              </MenuItem>
            )),
          ],
        }),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: row.original.icon_bg_color[0] || 'transparent',
              color: row.original.icon_font_color[0] || 'inherit',
              padding: '8px',
            }}
          >
            {row.original.icon[0] ? <i className={row.original.icon[0]} /> : 'None'}
          </Box>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 120,
      },
      {
        accessorKey: 'input_name',
        header: 'Input Type',
        size: 120,
      },
      {
        accessorKey: 'default_value',
        header: 'Default Value',
        size: 120,
      },
      {
        accessorKey: 'has_default_value',
        header: 'Has Default Value',
        size: 120,
        Cell: ({ cell }) => cell.getValue()?.[0]?.toString() || 'false',
      },
      {
        accessorKey: 'automove',
        header: 'Automove',
        size: 120,
        Cell: ({ row, cell }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span>{cell.getValue()?.[0] ? row.original.automove_destination?.[0] : ''}</span>
          </Box>
        ),
      },
      {
        accessorKey: 'delete',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="error"
            size="small"
            className="btn btn-danger btn-sm"
            onClick={() => {
              setDeleteType('vehicle_column');
              setDeleteId(row.original.vehicle_column_id[0]);
              setDeleteName(row.original.name[0]);
              setModalOpen(true);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [dispatch, iconList, tab],
  );

  const vehicleColumnsData = useMemo(
    () =>
      localVehicleColumns.map((vehicle_column) => ({
        vehicle_column_id: [vehicle_column.id],
        name: [vehicle_column.name],
        input_name: [vehicle_column.input_name],
        default_value: [vehicle_column.default_value],
        has_default_value: [vehicle_column.has_default_value],
        automove: [vehicle_column.automove],
        automove_destination: [vehicle_column.automove_destination],
        icon: [vehicle_column.icon || ''],
        icon_bg_color: [vehicle_column.icon_bg_color || ''],
        icon_font_color: [vehicle_column.icon_font_color || ''],
        org: [vehicle_column],
      })) || [],
    [localVehicleColumns],
  );

  const tabPropertiesData = useMemo(
    () =>
      tab?.tab_properties
        ?.slice()
        ?.sort((a, b) => a.order - b.order)
        ?.map((property) => ({
          id: [property.id],
          tab_id: [property.tab_id],
          tab_property_id: [property.id],
          vehicle_column_id: [property.vehicle_column_id],
          order: [property.order],
          name: [property.name],
          label: [property.label],
          required: [property.required],
          lockable: [property.lockable],
          notifications: [property.notifications],
          key: [property.key],
          value: [property.value],
          dealership: [property.dealership || ''],
        })) || [],
    [tab?.tab_properties],
  );

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setNewColumnName('');
    setInputName('');
    setAddColumnError(false);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewColumnName('');
    setInputName('');
    setAddColumnError(false);
  };

  const handleAddVehicleColumn = () => {
    if (!newColumnName.trim() || !inputName) {
      setAddColumnError(true);
      return;
    }

    const data = {
      name: newColumnName,
      input_name: inputName,
    };

    dispatch(addNewVehicleColumn(data, 'addVehicleColumnError')).then((resp) => {
      if (resp === 'success') {
        setAddColumnError(false);
        handleCloseAddModal();
      } else {
        const errorMessage = resp?.error?.invalid_params?.name?.[0] || 'An error occurred while adding the column';
        setAddColumnError(true);
      }
    });
  };

  const tabPropertiesTableRef = useRef();
  const vehicleColumnsTableRef = useRef();

  const handleRowOrderChange = (draggingRow, hoveredRow) => {
    const newData = [...tabPropertiesData];
    if (hoveredRow && draggingRow) {
      newData.splice(hoveredRow.index, 0, newData.splice(draggingRow.index, 1)[0]);
      const newOrder = newData.map((item) => item.tab_property_id[0]);
      dispatch(updateTabPropertiesOrder(newOrder, 'editTabPropertyError'));
    }
  };

  const handleConfirmDelete = () => {
    if (deleteType === 'tab_property') {
      dispatch(deleteTabProperty(deleteId, 'deleteTabPropertyError'));
    } else if (deleteType === 'vehicle_column') {
      dispatch(deleteVehicleColumn(deleteId, 'editVehicleColumnError'));
    }
    setModalOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
    setTableKey((prev) => prev + 1);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
  };

  if (!tab) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-warning" role="alert">
                  <i className="fa fa-exclamation-circle me-2" />
                  No tab selected or tab data is not available.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmationModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${deleteName} a ${deleteType === 'tab_property' ? 'tab property' : 'vehicle column'}?`}
      />
      <Dialog open={addModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add Vehicle Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Column Name"
            type="text"
            fullWidth
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            error={addColumnError && !newColumnName.trim()}
            helperText={addColumnError && !newColumnName.trim() ? 'Please enter a valid column name' : ''}
          />
          <FormControl fullWidth margin="dense" error={addColumnError && !inputName}>
            <InputLabel>Input Type</InputLabel>
            <Select
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              label="Input Type"
            >
              <MenuItem value="">Select Input Type</MenuItem>
              {inputTypesSorted.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {`${option.name} (${option.description})`}
                </MenuItem>
              ))}
            </Select>
            {addColumnError && !inputName && (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                Please select an input type
              </Box>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="error">
            Cancel
          </Button>
          <Button onClick={handleAddVehicleColumn} color="success">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fa fa-exclamation-circle me-2" />
          {error}
        </div>
      )}
      <div className="container-fluid">
        <div className="row">
          <div className="card col-md-5">
            <div className="card-body">
              <div className="d-flex flex-column">
                <div className="p-2">
                  <div className="col-12">
                    <small>
                      <i className="fa fa-info-circle text-info" />
                      {' '}
                      Drag and Drop rows to change order
                    </small>
                  </div>
                  <div className="p-2 list-group setscroll">
                    <ThemeProvider theme={tableTheme}>
                      <MaterialReactTable
                        tableRef={tabPropertiesTableRef}
                        columns={tabPropertiesColumns}
                        data={tabPropertiesData}
                        editDisplayMode="cell"
                        enableEditing
                        enableRowOrdering
                        enableRowDragging
                        enableSorting={false}
                        enableColumnFilters
                        initialState={{
                          showColumnFilters: true,
                        }}
                        muiRowDragHandleProps={({ table }) => ({
                          onDragEnd: () => {
                            const { draggingRow, hoveredRow } = table.getState();
                            if (hoveredRow && draggingRow) {
                              handleRowOrderChange(draggingRow, hoveredRow);
                            }
                          },
                        })}
                        sx={{ minHeight: '300px' }}
                      />
                    </ThemeProvider>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer" />
          </div>

          <div className="card col-md-7">
            <div className="card-header bg-dark text-white">
              <div className="col-6">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenAddModal}
                >
                  Create New Column
                </Button>
              </div>
            </div>
            <div className="card-body">
              <div className="d-flex flex-column">
                <div className="p-2 list-group setscroll">
                  <ThemeProvider theme={tableTheme}>
                    <MaterialReactTable
                      key={tableKey}
                      tableRef={vehicleColumnsTableRef}
                      columns={vehicleColumns}
                      data={vehicleColumnsData}
                      editDisplayMode="cell"
                      enableEditing
                      enableSorting={false}
                      enablePagination={false}
                      enableColumnFilters
                      initialState={{ showColumnFilters: true }}
                      state={{ expanded: expandedRows }}
                      onExpandedChange={(updater) => {
                        const newExpanded = typeof updater === 'function' ? updater(expandedRows) : updater;
                        setExpandedRows(newExpanded); // Changed to direct set, matching TabEditDepartmentProperties
                      }}
                      renderDetailPanel={({ row }) => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            justifyContent: 'space-around',
                          }}
                        >
                          <TabEditVehicleColumn vehicle_column_id={row.original.vehicle_column_id[0]} />
                        </Box>
                      )}
                    />
                  </ThemeProvider>
                </div>
              </div>
            </div>
            <div className="card-footer" />
          </div>
        </div>
      </div>
    </>
  );
};

export default TabEditTabProperties;